/* eslint-disable max-classes-per-file */
/* eslint-disable no-use-before-define */
import { BehaviorSubject } from 'rxjs';
import { share, switchMap } from 'rxjs/operators';
import { FRAME_NAME, sendMessageToSubFrame } from '@shell/utils/subFrames';
import {
    MessageSourceKey,
    Observable,
    SubscriptionLike,
    ObservableCreator,
    MessageSourceManager,
    PipeConfig,
} from '../interface';

/**
 * 根自定义消息源管理器
 */
export class RootCustomSourceManager<K extends MessageSourceKey = MessageSourceKey>
    implements MessageSourceManager<K> {
    private sourceSubjects: Record<K, BehaviorSubject<Observable<any>>>;

    private sources: Record<K, Observable<any>>;

    private registry: Partial<Record<K, SubscriptionLike>>;

    constructor() {
        this.sourceSubjects = {} as Record<K, BehaviorSubject<Observable<any>>>;
        this.sources = {} as Record<K, Observable<any>>;
        this.registry = {};
    }

    public useSource<T = any>(key: K, newSource: ObservableCreator<T>) {
        const source$ = typeof newSource === 'function' ? newSource() : newSource;

        if (!this.sourceSubjects[key]) {
            const sourceSubject$ = new BehaviorSubject(source$);
            this.sourceSubjects[key] = sourceSubject$;
            this.sources[key] = sourceSubject$.pipe(
                switchMap(source => source),
                share(),
            );
        } else {
            (this.sourceSubjects[key] as BehaviorSubject<Observable<any>>).next(source$);
        }
    }

    public getSource<T = any>(key: K): Observable<T> {
        if (!this.sources[key]) {
            this.useSource(key, new Observable());
        }
        return this.sources[key];
    }

    public registerSource(key: K): SubscriptionLike {
        const subscription = this.registry[key] as SubscriptionLike | undefined;
        if (subscription) {
            return subscription;
        }

        const newSubscription = this.getSource(key).subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, {
                type: 'shell:custom',
                key,
                data: message,
            });
        });
        this.registry[key] = newSubscription;
        return newSubscription;
    }

    public pipe(config?: PipeConfig<K>): MessageSourceManager<K> {
        return new CustomSourceManager(this, this.sources, config);
    }
}

/**
 * `CustomSourceManager` 也实现了 `MessageSourceManager`，但只对原始消息源进行加工操作，
 * 消息的生产和注册通过 `RootCustomSourceManager` 进行。
 */
export class CustomSourceManager<K extends MessageSourceKey = MessageSourceKey>
    implements MessageSourceManager<K> {
    private root: RootCustomSourceManager<K>;

    /** 消息源 */
    private sources: Record<K, Observable<any>>;

    constructor(
        root: RootCustomSourceManager<K>,
        sources: Record<K, Observable<any>>,
        pipeConfig = {} as PipeConfig<K>,
    ) {
        this.root = root;
        this.sources = {} as Record<K, Observable<any>>;
        ([...new Set([...Object.keys(sources), ...Object.keys(pipeConfig)])] as K[]).forEach(
            key => {
                const source$ = sources[key];
                const pipeFunction = pipeConfig[key];
                if (pipeFunction) {
                    this.sources[key] = pipeFunction(
                        // 如果传入的消息源中没有相应的 key，则通过根管理器初始化一个
                        source$ || root.getSource(key),
                    ).pipe(share());
                } else if (source$) {
                    this.sources[key] = source$;
                }
            },
        );
    }

    public getSource<T = any>(key: K): Observable<T> {
        if (!this.sources[key]) {
            this.sources[key] = this.root.getSource(key);
        }
        return this.sources[key];
    }

    public registerSource(key: K) {
        return this.root.registerSource(key);
    }

    public pipe(config?: PipeConfig<K>) {
        return new CustomSourceManager(this.root, this.sources, config);
    }
}
